import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  toDatetimeLocalValue,
  toLocalDateTimeString,
} from '../../utils/urgentDeadlineUtils';

const PRESETS = [
  { id: '15m', label: 'Next 15 min', minutes: 15 },
  { id: '1h', label: 'Next 1 hour', minutes: 60 },
  { id: '2h', label: 'Next 2 hours', minutes: 120 },
];

export const useUrgentDeadlineDialog = () => {
  const [open, setOpen] = useState(false);
  const resolverRef = useRef(null);

  const promptUrgentDeadline = useCallback(() => new Promise((resolve) => {
    resolverRef.current = resolve;
    setOpen(true);
  }), []);

  const finish = useCallback((deadlineIso) => {
    resolverRef.current?.(deadlineIso);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  const urgentDeadlineDialog = (
    <UrgentDeadlineDialog
      open={open}
      onCancel={() => finish(null)}
      onConfirm={(deadlineIso) => finish(deadlineIso)}
    />
  );

  return { promptUrgentDeadline, urgentDeadlineDialog };
};

const UrgentDeadlineDialog = ({ open, onCancel, onConfirm }) => {
  const [presetId, setPresetId] = useState('15m');
  const [customValue, setCustomValue] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const soon = new Date(Date.now() + 15 * 60 * 1000);
    setPresetId('15m');
    setCustomValue(toDatetimeLocalValue(soon));
    setError('');
  }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    let deadline;
    if (presetId === 'custom') {
      if (!customValue) {
        setError('Pick a date and time');
        return;
      }
      deadline = new Date(customValue);
    } else {
      const preset = PRESETS.find((item) => item.id === presetId);
      if (!preset) {
        setError('Select a deadline');
        return;
      }
      deadline = new Date(Date.now() + preset.minutes * 60 * 1000);
    }
    if (!Number.isFinite(deadline.getTime())) {
      setError('Invalid date and time');
      return;
    }
    if (deadline.getTime() <= Date.now() - 30 * 1000) {
      setError('Deadline must be in the future');
      return;
    }
    const iso = toLocalDateTimeString(deadline);
    if (!iso) {
      setError('Invalid date and time');
      return;
    }
    onConfirm(iso);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1300] overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Set urgent deadline
            </h3>
            <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
              Choose when this work must be finished. The assignee will see a countdown timer.
            </p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="grid grid-cols-1 gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => {
                    setPresetId(preset.id);
                    setError('');
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                    presetId === preset.id
                      ? 'border-red-400 bg-red-50 text-red-800 dark:border-red-500/60 dark:bg-red-950/40 dark:text-red-200'
                      : 'border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/60'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setPresetId('custom');
                  setError('');
                }}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                  presetId === 'custom'
                    ? 'border-red-400 bg-red-50 text-red-800 dark:border-red-500/60 dark:bg-red-950/40 dark:text-red-200'
                    : 'border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/60'
                }`}
              >
                Pick date &amp; time
              </button>
            </div>
            {presetId === 'custom' ? (
              <label className="block">
                <span className="block text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                  Deadline
                </span>
                <input
                  type="datetime-local"
                  value={customValue}
                  onChange={(event) => {
                    setCustomValue(event.target.value);
                    setError('');
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              </label>
            ) : null}
            {error ? <p className="text-[12px] text-red-600 dark:text-red-400">{error}</p> : null}
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-[13px] font-semibold rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-3 py-1.5 text-[13px] font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              Mark urgent
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UrgentDeadlineDialog;
